import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { labelPeriodicidade } from '../../constants/prestadorClassificacao';

function statusColor(status) {
  if (status === 'pago') return 'success';
  if (status === 'atrasado') return 'error';
  return 'warning';
}

function statusLabel(status) {
  if (status === 'pago') return 'Pago';
  if (status === 'atrasado') return 'Atrasado';
  return 'Pendente';
}

function formatDateBr(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

export default function ParcelasContratoDialog({
  open,
  onClose,
  prestador,
  unidadeSelecionada,
  onUpdated,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [nfFile, setNfFile] = useState(null);
  const [compFile, setCompFile] = useState(null);
  const [dataPagamento, setDataPagamento] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    if (!prestador?.id) return;
    setLoading(true);
    setError('');
    try {
      const url = withUnidade(`/api/prestadores/${prestador.id}/parcelas`, unidadeSelecionada);
      const res = await apiFetch(url);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Erro ao carregar parcelas');
      setData(json);
    } catch (e) {
      setError(e.message || 'Erro ao carregar parcelas');
    } finally {
      setLoading(false);
    }
  }, [prestador?.id, unidadeSelecionada]);

  useEffect(() => {
    if (open) {
      setPayingId(null);
      setNfFile(null);
      setCompFile(null);
      load();
    }
  }, [open, load]);

  const handleGerar = async () => {
    setError('');
    try {
      const url = withUnidade(`/api/prestadores/${prestador.id}/parcelas/gerar`, unidadeSelecionada);
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Erro ao gerar');
      if (json.skipped && json.reason === 'ha_pagas') {
        setError('Há parcelas pagas — não é possível regenerar automaticamente. Reabra as pagas se precisar.');
      }
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const handlePagar = async (parcela) => {
    setError('');
    try {
      const fd = new FormData();
      fd.append('dataPagamento', dataPagamento);
      if (nfFile) fd.append('notaFiscal', nfFile);
      if (compFile) fd.append('comprovante', compFile);
      const url = withUnidade(
        `/api/prestadores/${prestador.id}/parcelas/${parcela.id}/pagar`,
        unidadeSelecionada
      );
      const res = await apiFetch(url, { method: 'PUT', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Erro ao marcar como paga');
      setPayingId(null);
      setNfFile(null);
      setCompFile(null);
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleReabrir = async (parcela) => {
    if (!window.confirm(`Reabrir a parcela ${parcela.numero}?`)) return;
    try {
      const url = withUnidade(
        `/api/prestadores/${prestador.id}/parcelas/${parcela.id}/reabrir`,
        unidadeSelecionada
      );
      const res = await apiFetch(url, { method: 'PUT' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Erro ao reabrir');
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const parcelas = data?.parcelas || [];
  const resumo = data?.resumo;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          Parcelas — {prestador?.numeroContrato} · {prestador?.empresa}
          <Typography variant="body2" color="text.secondary">
            {labelPeriodicidade(data?.contrato?.periodicidade || prestador?.periodicidade)}
            {resumo ? ` · ${resumo.pagas}/${resumo.total} pagas` : ''}
            {resumo?.atrasadas ? ` · ${resumo.atrasadas} atrasada(s)` : ''}
          </Typography>
        </Box>
        <IconButton onClick={load} aria-label="Atualizar">
          <RefreshIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : parcelas.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Nenhuma parcela gerada ainda.
            </Typography>
            <Button variant="contained" onClick={handleGerar}>
              Gerar parcelas
            </Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Documentos</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parcelas.map((p) => (
                <TableRow
                  key={p.id}
                  sx={
                    p.status === 'atrasado'
                      ? { bgcolor: 'rgba(211,47,47,0.06)' }
                      : undefined
                  }
                >
                  <TableCell>{p.numero}</TableCell>
                  <TableCell>{formatDateBr(p.vencimento)}</TableCell>
                  <TableCell>
                    {Number(p.valor).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel(p.status)} color={statusColor(p.status)} />
                    {p.dataPagamento && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Pago em {formatDateBr(p.dataPagamento)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.notaFiscalUrl && (
                      <Button
                        size="small"
                        onClick={() => openAuthenticatedUpload(p.notaFiscalUrl)}
                      >
                        NF
                      </Button>
                    )}
                    {p.comprovanteUrl && (
                      <Button
                        size="small"
                        onClick={() => openAuthenticatedUpload(p.comprovanteUrl)}
                      >
                        Comp.
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {p.status !== 'pago' ? (
                      payingId === p.id ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                          <TextField
                            type="date"
                            size="small"
                            label="Data pagamento"
                            value={dataPagamento}
                            onChange={(e) => setDataPagamento(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <Button component="label" size="small" variant="outlined">
                            {nfFile ? nfFile.name : 'Nota fiscal'}
                            <input
                              hidden
                              type="file"
                              accept=".pdf,image/*"
                              onChange={(e) => setNfFile(e.target.files?.[0] || null)}
                            />
                          </Button>
                          <Button component="label" size="small" variant="outlined">
                            {compFile ? compFile.name : 'Comprovante'}
                            <input
                              hidden
                              type="file"
                              accept=".pdf,image/*"
                              onChange={(e) => setCompFile(e.target.files?.[0] || null)}
                            />
                          </Button>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" onClick={() => setPayingId(null)}>
                              Cancelar
                            </Button>
                            <Button size="small" variant="contained" color="success" onClick={() => handlePagar(p)}>
                              Confirmar pago
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => setPayingId(p.id)}>
                          Marcar paga
                        </Button>
                      )
                    ) : (
                      <Tooltip title="Voltar para pendente">
                        <Button size="small" onClick={() => handleReabrir(p)}>
                          Reabrir
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        {parcelas.length > 0 && (
          <Button onClick={handleGerar} sx={{ mr: 'auto' }}>
            Regenerar pendentes
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
